import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { useAuthStore } from '../../store/authStore';
import useIdleLogout from '../../hooks/useIdleLogout';
import { pbxWS } from '../../services/websocket';
import api from '../../services/api';
import { ConfirmProvider } from '../common/ConfirmDialog';
import { isMultiCompany } from '../../utils/multiCompany';
import {
  LayoutDashboard, Phone, PhoneCall, PhoneForwarded, Voicemail,
  Users, Settings, LogOut, Menu, X, GitBranch, MessageSquare,
  List, Mic, Book, Video, Globe, Server, ChevronRight,
  PhoneOff, Shield, Network, Layers, Activity, Plug, AppWindow,
  FileText, ClipboardList, Building2, PackageCheck, Eye, LifeBuoy, Bot
} from 'lucide-react';

const navSections = [
  {
    label: null,
    items: [
      { path: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true, cap: 'dashboard.view' },
      { path: '/monitor', icon: Activity, label: 'Monitor', cap: 'monitor.view' },
      { path: '/webphone', icon: PhoneCall, label: 'Marcador', external: true },
    ],
  },
  {
    label: 'Telefonía',
    items: [
      { path: '/extensions', icon: Phone, label: 'Extensiones', cap: 'extensions.view' },
      { path: '/trunks', icon: Network, label: 'Troncales', cap: 'trunks.manage' },
      { path: '/queues', icon: List, label: 'Colas ACD', cap: 'queues.manage' },
      { path: '/ring-groups', icon: PhoneForwarded, label: 'Grupos Timbrado', cap: 'dialplan.manage' },
      { path: '/ivr', icon: GitBranch, label: 'IVR', cap: 'ivr.manage' },
    ],
  },
  {
    label: 'Servicios',
    items: [
      { path: '/voicemail', icon: Voicemail, label: 'Buzón de Voz' },
      { path: '/conferences', icon: Video, label: 'Conferencias' },
      { path: '/phonebook', icon: Book, label: 'Agenda' },
      { path: '/messaging', icon: MessageSquare, label: 'Mensajería', cap: 'messaging.send' },
      { path: '/integrations', icon: Plug, label: 'Integraciones', cap: 'system.manage' },
    ],
  },
  {
    label: 'Reportes',
    items: [
      { path: '/cdr', icon: PhoneOff, label: 'Registro Llamadas', cap: 'cdr.view' },
      { path: '/events', icon: ClipboardList, label: 'Registro Eventos', cap: 'dashboard.view' },
      { path: '/recordings', icon: Mic, label: 'Grabaciones', cap: 'recordings.listen' },
    ],
  },
  {
    label: 'Apps',
    items: [
      { path: '/apps', icon: AppWindow, label: 'Apps' },
      { path: '/support-tickets', icon: LifeBuoy, label: 'Tickets de Soporte', multiOnly: true },
    ],
  },
  {
    label: 'Administración',
    items: [
      // multiOnly = administración del PROPIETARIO: solo existe en una instalación Multi
      // Company; el panel de cliente (1223/1761, instalaciones nuevas) no las muestra.
      { path: '/companies', icon: Building2, label: 'Empresas', roles: ['superadmin'], multiOnly: true },
      { path: '/plans', icon: PackageCheck, label: 'Planes', roles: ['superadmin'], multiOnly: true },
      { path: '/ai-agent', icon: Bot, label: 'Agente IA', roles: ['superadmin'], multiOnly: true },
      { path: '/dialplan', icon: Layers, label: 'Plan Marcado', cap: 'dialplan.manage' },
      { path: '/users', icon: Users, label: 'Usuarios', cap: 'users.manage' },
      { path: '/settings', icon: Settings, label: 'Configuración', cap: 'system.manage' },
      { path: '/system', icon: Server, label: 'Sistema', cap: 'system.manage' },
    ],
  },
];

// Sidebar del System Owner: un menú propio y curado (no el de operación
// diaria filtrado) — 4 secciones fijas a pedido explícito. "Seguridad
// global" vive dentro de Sistema (ver SystemPage.js), no es un item aparte.
const SUPERADMIN_NAV_SECTIONS = [
  {
    label: null,
    items: [
      { path: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    ],
  },
  {
    label: 'Administración',
    items: [
      { path: '/companies', icon: Building2, label: 'Empresas' },
      { path: '/plans', icon: PackageCheck, label: 'Planes' },
      { path: '/ai-agent', icon: Bot, label: 'Agente IA' },
      { path: '/dialplan', icon: Layers, label: 'Plan Marcado' },
      { path: '/users', icon: Users, label: 'Usuarios' },
      { path: '/settings', icon: Settings, label: 'Configuración' },
      { path: '/system', icon: Server, label: 'Sistema' },
    ],
  },
  {
    label: 'Reportes',
    items: [
      { path: '/events', icon: ClipboardList, label: 'Registro Eventos' },
    ],
  },
  {
    label: 'Soporte',
    items: [
      { path: '/support-tickets', icon: LifeBuoy, label: 'Tickets de Soporte' },
    ],
  },
];

// Selector de empresa activa — solo System Owner. Al cambiar, se limpia toda
// la caché de react-query: cada página vuelve a pedir sus datos con el nuevo
// X-Company-Id (interceptor en services/api.js).
function CompanySwitcher() {
  const qc = useQueryClient();
  const { selectedCompanyId, setSelectedCompanyId } = useAuthStore();
  const { data: companies = [] } = useQuery('companies', () => api.get('/companies').then(r => r.data));

  return (
    <select
      value={selectedCompanyId || ''}
      onChange={(e) => { setSelectedCompanyId(e.target.value ? Number(e.target.value) : null); qc.clear(); }}
      className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-xl px-3 py-2 hover:bg-gray-700 cursor-pointer"
      title="Empresa activa (Multi Company)"
    >
      <option value="">Todas las empresas</option>
      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select>
  );
}

// Soporte: banner fijo mientras el System Owner está "dentro" de la cuenta
// de otro usuario. Un solo botón para volver a su propia sesión.
function ImpersonationBanner() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { impersonatorSession, user, stopImpersonation } = useAuthStore();
  if (!impersonatorSession) return null;

  return (
    <div className="h-10 bg-purple-900/60 border-b border-purple-700 flex items-center justify-center gap-3 px-4 flex-shrink-0 text-sm text-purple-200">
      <Eye size={15} />
      <span>Modo soporte: viendo el panel como <strong>{user?.username}</strong></span>
      <button
        onClick={() => { stopImpersonation(); qc.clear(); navigate('/'); }}
        className="ml-2 px-2 py-0.5 rounded-md bg-purple-700 hover:bg-purple-600 text-white text-xs font-medium"
      >
        Volver a mi cuenta
      </button>
    </div>
  );
}

// Cierra un menú desplegable con clic fuera o Escape (/apple-design: salida evidente).
function useDismiss(ref, open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, ref, onClose]);
}

// Menú de cuenta: el avatar abre un menú con quién eres, el estado de conexión EN
// TEXTO (antes solo un punto de color) y "Cerrar sesión" como única salida. Antes el
// avatar y la tarjeta del sidebar cerraban la sesión al primer clic.
function AccountMenu({ user, connected, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);
  const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.username;

  return (
    <div ref={ref} className="relative ml-1">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}
        aria-label={`Menú de cuenta de ${name}`} className="relative rounded-full">
        <div className="w-10 h-10 rounded-full bg-gray-500 hover:bg-gray-400 flex items-center justify-center text-white font-semibold text-sm transition-colors">
          {user?.firstName?.[0]}{user?.lastName?.[0]}
        </div>
        <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-gray-900 ${connected ? 'bg-green-500' : 'bg-gray-500'}`} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-60 bg-gray-900 border border-gray-800 rounded-xl shadow-xl z-50 py-1">
          <div className="px-4 py-3 border-b border-gray-800">
            <div className="text-sm font-medium text-gray-100 truncate">{name}</div>
            <div className="text-sm text-gray-400 truncate">{user?.role}</div>
            <div className="mt-1 flex items-center gap-1.5 text-sm text-gray-400">
              <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-gray-500'}`} />
              {connected ? 'En línea' : 'Desconectado'}
            </div>
          </div>
          <button type="button" role="menuitem" onClick={onLogout} autoFocus
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-200 hover:bg-gray-800 text-left">
            <LogOut size={16} /> Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, token, logout } = useAuthStore();
  useIdleLogout();                 // cierra sesión tras 5 min de inactividad
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [wsStatus, setWsStatus] = useState('disconnected');
  const [activeCallsCount, setActiveCallsCount] = useState(0);

  // Layout según el espacio disponible (/apple-design, layout.md): en pantallas
  // anchas el sidebar es fijo y se puede colapsar a iconos; por debajo de lg es un
  // cajón que se abre sobre el contenido y se cierra al navegar, con Escape o
  // tocando fuera.
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);
  const expanded = !isDesktop || sidebarOpen;      // con etiquetas (cajón siempre; escritorio según colapso)
  const menuOpen = isDesktop ? sidebarOpen : mobileOpen;

  useEffect(() => {
    if (token) {
      pbxWS.connect(token);
      const offConnected = pbxWS.on('connected', () => setWsStatus('connected'));
      const offDisconnected = pbxWS.on('disconnected', () => setWsStatus('disconnected'));
      // El contador viene SOLO del snapshot periódico (llamadas reales por
      // linkedid). No se deriva de call.new/hangup: esos llegan 2 veces por
      // llamada (agente + troncal) y hacían que el contador mostrara el doble.
      const offSnapshot = pbxWS.on('dashboard.snapshot', data => {
        setActiveCallsCount(data?.activeChannels || 0);
      });

      return () => { offConnected(); offDisconnected(); offSnapshot(); };
    }
  }, [token]);

  const handleLogout = async () => {
    pbxWS.disconnect();
    await logout();
    navigate('/login');
  };

  // Muestra un item si: no tiene restricción, o el usuario tiene la capacidad.
  // Fallback por rol si aún no hay permisos cargados.
  const canSee = (item) => {
    if (item.multiOnly && !isMultiCompany()) return false;
    if (!item.cap && !item.roles) return true;
    if (user?.role === 'superadmin') return true;
    if (item.cap) {
      if (user?.permissions) return !!user.permissions[item.cap];
      return ['admin', 'superadmin'].includes(user?.role);
    }
    return item.roles.includes(user?.role);
  };
  // El System Owner de Multi Company tiene su propio menú curado
  // (SUPERADMIN_NAV_SECTIONS) porque no opera el día a día — administra
  // empresas inquilinas, no contesta llamadas. Pero 'superadmin' en una
  // instancia de una sola empresa (1223, 1761) ES quien opera todo: ocultarle
  // Monitor/Extensiones/Grabaciones rompió el panel ahí al desplegar este
  // mismo archivo (30-sep-2026). Bandera por instancia, no solo el rol —
  // window.__PBX_CONFIG__.multiCompany solo es true en voxcentral.
  const filteredSections = (user?.role === 'superadmin' && window.__PBX_CONFIG__?.multiCompany)
    ? SUPERADMIN_NAV_SECTIONS
    : navSections.map(section => ({
        ...section,
        items: section.items.filter(canSee),
      })).filter(section => section.items.length > 0);

  return (
    <ConfirmProvider>
    <div className="flex h-screen bg-gray-950 overflow-hidden">
      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setMobileOpen(false)} aria-hidden="true" />}
      {/* Sidebar */}
      <aside id="sidebar" className={`fixed inset-y-0 left-0 z-40 w-64 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:static lg:translate-x-0 ${expanded ? 'lg:w-64' : 'lg:w-16'} bg-gray-900 border-r border-gray-800 flex flex-col transition-all duration-200 flex-shrink-0`}>
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-gray-800 gap-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Phone size={16} className="text-white" />
          </div>
          {expanded && (
            <div>
              <div className="font-display font-semibold text-white text-sm tracking-tight">{window.__PBX_CONFIG__?.brandName || 'PBX Virtual'}</div>
              {/* text-muted (#6b7280) en gray-900 da 3.7:1, bajo el 4.5:1
                  de WCAG AA; text-gray-400 sí cumple (/impeccable critique,
                  30-sep-2026 — P1, medido en vivo). */}
              <div className="text-sm text-gray-400">Sistema Telefónico</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2 px-2">
          {filteredSections.map((section, si) => (
            <div key={si} className={si > 0 ? 'mt-1' : ''}>
              {section.label && expanded && (
                <div className="px-2 pt-3 pb-1">
                  {/* text-[11px] es px literal, no lo toca el 87.5% global:
                      medía exactamente 10px, bajo el piso de 11px
                      (/impeccable critique, 30-sep-2026 — P1). */}
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 select-none">
                    {section.label}
                  </span>
                </div>
              )}
              {section.label && !expanded && si > 0 && (
                <div className="my-1 border-t border-gray-800/60" />
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => item.external ? (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => window.open(item.path, '_blank', 'noopener,noreferrer')}
                    className={`nav-item w-full text-left ${!expanded ? 'justify-center px-2' : ''}`}
                    title={!expanded ? `${item.label} (pestaña nueva)` : ''}
                  >
                    <item.icon size={18} className="flex-shrink-0" />
                    {expanded && <span>{item.label}</span>}
                  </button>
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.exact}
                    className={({ isActive }) =>
                      `nav-item ${isActive ? 'active' : ''} ${!expanded ? 'justify-center px-2' : ''}`
                    }
                    title={!expanded ? item.label : ''}
                  >
                    <item.icon size={18} className="flex-shrink-0" />
                    {expanded && <span>{item.label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer: quién eres. Cerrar sesión vive en el menú de la cuenta (barra superior). */}
        <div className="p-3 border-t border-gray-800">
          <div className={`flex items-center gap-3 p-2 ${!expanded ? 'justify-center' : ''}`}
            title={!expanded ? `${user?.firstName || ''} ${user?.lastName || ''}` : undefined}>
            <div className="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            {expanded && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-200 truncate">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="text-sm text-gray-400 truncate">{user?.role}</div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <ImpersonationBanner />
        {/* Topbar */}
        <header className="h-16 bg-gray-900 border-b border-gray-800 flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => (isDesktop ? setSidebarOpen(!sidebarOpen) : setMobileOpen(!mobileOpen))}
              aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={menuOpen}
              aria-controls="sidebar"
              className="text-gray-400 hover:text-gray-200 p-1.5 rounded-lg hover:bg-gray-800"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* Selector de empresa (Multi Company) — solo System Owner */}
            {user?.role === 'superadmin' && isMultiCompany() && <CompanySwitcher />}

            {/* Llamadas activas (solo cuando hay) */}
            {activeCallsCount > 0 && (
              <div className="flex items-center gap-2 bg-green-900/30 border border-green-800 px-3 py-1.5 rounded-lg mr-1">
                <span className="status-dot online animate-pulse" />
                <span className="text-green-400 text-sm font-medium">
                  {activeCallsCount} activa{activeCallsCount !== 1 ? 's' : ''}
                </span>
              </div>
            )}

            {/* Softphone — abre en su propia pestaña (única que registra la extensión) */}
            {user?.extensionNumber && (
              <button onClick={() => window.open('/webphone', '_blank', 'noopener,noreferrer')}
                className="w-10 h-10 rounded-xl bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center transition-colors"
                title={`Softphone · Ext. ${user.extensionNumber} (pestaña nueva)`}>
                <PhoneCall size={18} />
              </button>
            )}

            {/* Softphone de escritorio retirado: telefonía solo desde el panel web. */}

            {/* Registro de llamadas */}
            <button onClick={() => navigate('/cdr')}
              className="w-10 h-10 rounded-xl bg-gray-800 hover:bg-gray-700 text-gray-300 flex items-center justify-center transition-colors"
              title="Registro de llamadas">
              <FileText size={18} />
            </button>

            {/* Menú de cuenta (nombre, estado de conexión en texto, cerrar sesión) */}
            <AccountMenu user={user} connected={wsStatus === 'connected'} onLogout={handleLogout} />
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-gray-950">
          <div className="p-4 sm:p-6">
            {/* Las páginas se cargan bajo demanda: el menú se queda mientras baja el trozo */}
            <Suspense fallback={<div role="status" className="text-sm text-gray-500">Cargando…</div>}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
    </ConfirmProvider>
  );
}
